import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

export default function FeaturesDatacenter() {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const features = useMemo(() => [
    {
      icon: "ri-server-line",
      title: t("dc.features.f1.title"),
      description: t("dc.features.f1.desc"),
      stat: t("dc.features.f1.stat"),
      statLabel: t("dc.features.f1.statLabel"),
    },
    {
      icon: "ri-plug-line",
      title: t("dc.features.f2.title"),
      description: t("dc.features.f2.desc"),
      stat: t("dc.features.f2.stat"),
      statLabel: t("dc.features.f2.statLabel"),
    },
    {
      icon: "ri-temp-cold-line",
      title: t("dc.features.f3.title"),
      description: t("dc.features.f3.desc"),
      stat: t("dc.features.f3.stat"),
      statLabel: t("dc.features.f3.statLabel"),
    },
    {
      icon: "ri-router-line",
      title: t("dc.features.f4.title"),
      description: t("dc.features.f4.desc"),
      stat: t("dc.features.f4.stat"),
      statLabel: t("dc.features.f4.statLabel"),
    },
    {
      icon: "ri-shield-check-line",
      title: t("dc.features.f5.title"),
      description: t("dc.features.f5.desc"),
      stat: t("dc.features.f5.stat"),
      statLabel: t("dc.features.f5.statLabel"),
    },
    {
      icon: "ri-line-chart-line",
      title: t("dc.features.f6.title"),
      description: t("dc.features.f6.desc"),
      stat: t("dc.features.f6.stat"),
      statLabel: t("dc.features.f6.statLabel"),
    },
  ], [t]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="features" className="relative py-16 md:py-24 bg-background-950 overflow-hidden">
      {/* Humanized background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Data%20center%20technicians%20inspecting%20server%20racks%20together%20in%20a%20modern%20facility%2C%20warm%20human%20collaboration%2C%20professional%20editorial%20photography%2C%20human%20centered%2C%20soft%20depth%20of%20field&width=900&height=506&seq=dc-features-human-bg-01&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center opacity-[0.14]"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-950 via-background-950/40 to-background-950" />
      </div>
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary-400/25 to-transparent" />

      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(var(--primary-500) / 0.5) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.5) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-primary-500/4 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[350px] h-[350px] rounded-full bg-accent-500/3 blur-3xl pointer-events-none" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent-400/20 bg-accent-500/8 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
            <span className="text-[10px] font-label font-semibold text-accent-300 uppercase tracking-[0.2em]">
              {t("dc.features.tag")}
            </span>
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-50 mb-3 tracking-tight">
            {t("dc.features.title")} <span className="text-accent-400">{t("dc.features.highlight")}</span>
          </h2>
          <p className="text-sm text-foreground-400/60 max-w-lg mx-auto leading-relaxed">
            {t("dc.features.subtitle")}
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((feature, idx) => (
            <div
              key={feature.title}
              className={`group relative overflow-hidden rounded-2xl border border-foreground-100/8 bg-background-950/40 backdrop-blur-sm p-5 hover:border-primary-400/25 hover:bg-background-900/50 transition-all duration-500 hover:-translate-y-1 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${150 + idx * 90}ms` }}
            >
              <div className="absolute top-0 left-3 right-3 h-px">
                <div className="h-full w-8 bg-gradient-to-r from-primary-400 to-accent-400 opacity-25 group-hover:w-full group-hover:opacity-80 transition-all duration-700" />
              </div>

              <div className="relative z-10">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-500/10 border border-primary-400/15 text-primary-400 mb-4 group-hover:bg-primary-500/18 group-hover:text-primary-300 group-hover:border-primary-400/25 transition-all duration-300">
                  <i className={`${feature.icon} text-lg`} />
                </div>

                <div className="flex items-baseline gap-2 mb-2">
                  <h3 className="font-heading font-semibold text-base text-foreground-100 group-hover:text-primary-300 transition-colors">
                    {feature.title}
                  </h3>
                  <span className="text-[10px] text-primary-400/60 opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-up-line" />
                  </span>
                </div>

                <p className="text-sm text-foreground-400/60 leading-relaxed mb-4">
                  {feature.description}
                </p>

                <div className="flex items-center gap-2 pt-3 border-t border-foreground-100/5">
                  <span className="font-heading font-bold text-sm text-primary-400">{feature.stat}</span>
                  <span className="text-[10px] font-label text-foreground-500/40 uppercase tracking-wider">{feature.statLabel}</span>
                </div>
              </div>

              <div className="absolute bottom-2 right-2 w-2.5 h-2.5 border-b border-r border-primary-400/0 group-hover:border-primary-400/15 transition-all duration-500 rounded-br-sm" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}